import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDayPass } from '@/contexts/DayPassContext';
import { Button } from '@/components/ui/button';
import { Calendar, Phone, Mail, MessageCircle, Clock, ArrowRight } from 'lucide-react';
import { trackActivityContact } from '@/utils/analytics';

const DayGuestCTAContact: React.FC = () => {
    const { t } = useLanguage();
    const { setIsTrayOpen } = useDayPass();

    const contactMethods = [
        {
            icon: Phone,
            label: t('activities.ctaContact.contactMethods.phone.label'),
            value: '+41 33 841 00 21',
            action: 'tel:+41338410021',
            type: 'phone' as const,
        },
        {
            icon: Mail,
            label: t('activities.ctaContact.contactMethods.email.label'),
            value: 'info@beatenberg-resortspa.ch',
            action: 'mailto:info@beatenberg-resortspa.ch',
            type: 'email' as const,
        },
        {
            icon: MessageCircle,
            label: t('activities.ctaContact.contactMethods.whatsapp.label'),
            value: t('activities.ctaContact.contactMethods.whatsapp.value'),
            action: 'https://wa.me/41766220122',
            type: 'whatsapp' as const,
        },
    ];

    const handleContactClick = (method: typeof contactMethods[0]) => {
        // Use activity contact events instead of concierge
        // Since this is on Activities page and related to day pass inquiries
        trackActivityContact(
            'activities_cta', // activity_id
            'Activities CTA Section', // activity_name
            method.type,
            method.value,
            'activities_cta_direct' // contact_source
        );
    };

    return (
        <>
            <section className="relative bg-gradient-alpine py-16 md:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                {/* Decorative gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-alpine-gold/90 via-alpine-gold to-charcoal-gray/20" />
                
                {/* Background ornament */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-[url('/ornament-gold.svg')] bg-no-repeat bg-right-top opacity-[0.05] pointer-events-none z-0" />

                <div className="max-w-7xl mx-auto relative z-10">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12 md:mb-16"
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-playfair text-elegant-white mb-4 leading-tight uppercase">
                            {t('activities.ctaContact.title')}
                        </h2>
                        <p className="text-lg md:text-xl text-elegant-white/90 font-lato leading-relaxed max-w-3xl mx-auto">
                            {t('activities.ctaContact.subtitle')}
                        </p>
                    </motion.div>

                    {/* Two-column layout: CTAs left, Contact info right */}
                    <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
                        {/* Left Column: Primary CTAs */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="flex flex-col gap-6"
                        >
                            <h3 className="text-2xl md:text-3xl font-playfair text-elegant-white mb-2">
                                {t('activities.ctaContact.ctaTitle')}
                            </h3>
                            <p className="text-elegant-white/80 font-lato text-base md:text-lg mb-6">
                                {t('activities.ctaContact.ctaDescription')}
                            </p>

                            {/* Primary CTA Button */}
                            <div className="flex flex-col gap-4">
                                {/* Book Day Pass Button */}
                                <Button
                                    onClick={() => {
                                        setIsTrayOpen(true, 'cta_section');
                                    }}
                                    className="w-full bg-elegant-white text-charcoal-gray hover:bg-elegant-white/90 px-6 py-6 text-base md:text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                    trackingType="none"
                                >
                                    <Calendar size={20} className="mr-2" />
                                    {t('activities.dayPass.dayPassPricing.cta')}
                                    <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </div>
                        </motion.div>

                        {/* Right Column: Contact Information */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="flex flex-col gap-8"
                        >
                            <div>
                                {/* Contact Methods */}
                                <div className="space-y-4 mb-8">
                                    {contactMethods.map((method, index) => {
                                        const Icon = method.icon;
                                        return (
                                            <motion.a
                                                key={index}
                                                href={method.action}
                                                onClick={() => handleContactClick(method)}
                                                className={`contact-action-track flex items-center gap-4 p-4 bg-elegant-white/10 backdrop-blur-sm border border-elegant-white/20 rounded-xl hover:bg-elegant-white/20 transition-all duration-300 group ${
                                                    method.type === 'whatsapp' ? 'cursor-pointer' : ''
                                                }`}
                                                {...(method.type === 'whatsapp' ? {
                                                    target: '_blank',
                                                    rel: 'noopener noreferrer'
                                                } : {})}
                                            >
                                                <div className="w-12 h-12 bg-elegant-white/20 rounded-full flex items-center justify-center group-hover:bg-elegant-white/30 transition-colors">
                                                    <Icon className="w-5 h-5 text-elegant-white" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-elegant-white/80 font-lato text-sm mb-1">
                                                        {method.label}
                                                    </p>
                                                    <p className="text-elegant-white font-lato text-base font-semibold">
                                                        {method.value}
                                                    </p>
                                                </div>
                                                <ArrowRight className="w-5 h-5 text-elegant-white/60 group-hover:text-elegant-white group-hover:translate-x-1 transition-all" />
                                            </motion.a>
                                        );
                                    })}
                                </div>

                                {/* Operating Hours */}
                                <div className="bg-elegant-white/10 backdrop-blur-sm border border-elegant-white/20 rounded-xl p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-elegant-white/20 rounded-full flex items-center justify-center shrink-0">
                                            <Clock className="w-5 h-5 text-elegant-white" />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-elegant-white font-playfair text-lg mb-2">
                                                {t('activities.ctaContact.operatingHours.title')}
                                            </h4>
                                            <div className="space-y-1 text-elegant-white/90 font-lato text-sm">
                                                <p>{t('activities.ctaContact.operatingHours.days')}</p>
                                                <p>{t('activities.ctaContact.operatingHours.hours')}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-elegant-white/30 to-transparent" />
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-elegant-white/30 to-transparent" />
            </section>
        </>
    );
};

export default DayGuestCTAContact;
